import BodyText, { BODY_TEXT_SIZES } from "@shared/ui/BodyText";
import Button, { BUTTON_SIZES, BUTTON_VARIANTS } from "@shared/ui/Button";
import { Input } from "@shared/ui/Inputs";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useIntl } from "react-intl";

import EmptySpace from "~/components/_emptySpace";

const CustomNumberField = ({
  field,
  handleTextFieldSave,
  isEditable = true,
  dropdownClassname = "",
}) => {
  const { messages } = useIntl();

  const triggerRef = useRef();
  const menuRef = useRef();
  const [newValue, setNewValue] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const isSaveDisabled =
    !String(newValue?.[field?._id])?.length ||
    newValue?.[field?._id] === field?.value;

  const toggle = () => {
    setIsOpen((prev) => {
      if (!prev && !newValue[field?._id]?.length) {
        newValue[field?._id] = field?.value;
      }
      if (!prev && triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setPos({ top: rect.bottom + 4, left: rect.left });
      }

      return !prev;
    });
  };

  const updateTextField = (textFields) => {
    newValue[field?._id] = textFields;
    toggle();
  };

  useEffect(() => {
    if (!isOpen) return;
    const onClick = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setIsOpen(false);
    };
    const onScroll = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      setIsOpen(false);
    };
    const t = setTimeout(
      () => document.addEventListener("mousedown", onClick),
      0,
    );
    window.addEventListener("scroll", onScroll, true);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", onClick);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [isOpen]);

  return (
    <>
      <div className="flex items-center gap-sm py-sm custom-field-row">
        <BodyText
          size={BODY_TEXT_SIZES.X_SMALL}
          color="text-secondary"
          title={field?.label}
          className="w-[140px] flex-shrink-0 overflow-hidden text-ellipsis whitespace-nowrap no-styles"
        >
          {field?.label}:
        </BodyText>
        <div className="flex-1 min-w-0">
          <div className={`c-dropdown ${isOpen ? "c-dropdown--shown" : ""}`}>
            <button
              ref={triggerRef}
              className={`c-textarea-btn u-width-100 ${
                !isEditable
                  ? "pointer-events-none !bg-transparent"
                  : "cursor-pointer"
              }`}
              onClick={() => {
                if (!isEditable) return;
                toggle();
              }}
            >
              <span className={field?.value ? "u-text-color-gray-v2-100" : ""}>
                {field?.value
                  ? field.value
                  : `${messages?.common?.emptyProperty}`}
              </span>
            </button>
            {isOpen &&
              createPortal(
                <div
                  ref={(node) => {
                    menuRef.current = node;
                    if (node) {
                      node.style.setProperty("position", "fixed", "important");
                      node.style.setProperty(
                        "top",
                        `${pos.top}px`,
                        "important",
                      );
                      node.style.setProperty(
                        "left",
                        `${pos.left}px`,
                        "important",
                      );
                      node.style.setProperty("z-index", "9999", "important");
                      node.style.setProperty(
                        "display",
                        "inline-flex",
                        "important",
                      );
                    }
                  }}
                  className={
                    "c-dropdown-menu customize-field-dropdown property-userDropdownMenu flex-col " +
                    dropdownClassname
                  }
                >
                  <div style={{ width: "100%" }}>
                    <EmptySpace height="8px" />
                    <Input
                      keyId={field?._id}
                      type="number"
                      placeholder={field?.label}
                      value={newValue[field?._id] || ""}
                      onChange={(e) => {
                        if (!e.target.value) {
                          setNewValue({ [field?._id]: "" });
                          return;
                        }
                        const value =
                          e.target.value?.length <= 15
                            ? parseFloat(e.target.value)
                            : parseFloat(e.target.value?.slice(0, 15));
                        setNewValue({ [field?._id]: value });
                      }}
                    />
                    <EmptySpace height="8px" />
                    <div className="flex justify-end">
                      <Button
                        variant={BUTTON_VARIANTS.LINK}
                        size={BUTTON_SIZES.SMALL}
                        text={messages?.common?.discard}
                        onClick={(e) => {
                          if (!field?.value) {
                            setIsOpen(false);
                            return;
                          }
                          handleTextFieldSave(
                            e,
                            { [field?._id]: "" },
                            field?._id,
                            updateTextField,
                          );
                        }}
                      />

                      <Button
                        size={BUTTON_SIZES.SMALL}
                        text={messages?.common?.save}
                        disabled={isSaveDisabled}
                        className="ml-md"
                        onClick={(e) => {
                          handleTextFieldSave(
                            e,
                            newValue,
                            field?._id,
                            updateTextField,
                          );
                        }}
                      />
                    </div>
                  </div>
                </div>,
                document.body,
              )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CustomNumberField;
